/** The raw case fields both formatters read. */
export type CaseLawDisplayFields = {
  title: string
  citation: string | null
  jurisdiction_code: string
  /* ISO date ("YYYY-MM-DD"); anything else formats as date-unknown. */
  decide_date: string | null
  docket: string | null
}

/** Bluebook month abbreviations, indexed by month number - 1. */
const kBluebookMonthLabels = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'June', 'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.']

/** A date parenthetical some citations already end in, e.g. the "(9th Cir. 2012)" of "123 F.3d 456 (9th Cir. 2012)". */
const kTrailingDateParentheticalRegex = /\s+\([^()]*\b\d{4}\)\s*$/

/**
 * Bluebook-style case title: "Title, Citation (Court Year)", or the docket fallback
 * "Title, No. docket (Court Mon. D, YYYY)" when the case has no reporter citation.
 * SCOTUS parentheticals omit the court ("(1992)", never "(U.S. 1992)"), and a citation that already
 * ends in a date parenthetical never gets a second one.
 *
 * Case payloads carry raw fields rather than a preformatted string, so the client follows
 * the same formatting rules as the backend source labeler (`paxton_app/agent_v2/citations/bluebook.py`).
 */
export function formatCaseLawBluebookTitle(fields: CaseLawDisplayFields): string {
  const decideDate = parseIsoDate(fields.decide_date)
  const isScotus = fields.jurisdiction_code === 'U.S.'

  if (fields.citation) {
    let citation = fields.citation
    const trailingDate = kTrailingDateParentheticalRegex.exec(citation)
    if (trailingDate) {
      // Without a decide date, the citation's own parenthetical says more than a court-only one.
      if (!decideDate) return `${fields.title}, ${citation}`.trim()
      citation = citation.slice(0, trailingDate.index)
    }

    let parenthetical: string
    if (isScotus) {
      parenthetical = decideDate ? `(${decideDate.year})` : ''
    } else {
      parenthetical = decideDate ? `(${fields.jurisdiction_code} ${decideDate.year})` : `(${fields.jurisdiction_code})`
    }
    return `${fields.title}, ${citation} ${parenthetical}`.trim()
  }

  if (fields.docket) {
    const docket = fields.docket.toLowerCase().includes('no. ') ? fields.docket : `No. ${fields.docket}`
    const parenthetical = decideDate
      ? `(${fields.jurisdiction_code} ${kBluebookMonthLabels[decideDate.month - 1]} ${decideDate.day}, ${decideDate.year})`
      : `(${fields.jurisdiction_code})`
    return `${fields.title}, ${docket} ${parenthetical}`
  }

  return fields.title
}

/** The metadata line shown under a case title: court, decide year, and docket, each only when known. */
export function formatCaseLawMetadataLine(fields: CaseLawDisplayFields): string | null {
  const parts: string[] = []
  if (fields.jurisdiction_code) parts.push(fields.jurisdiction_code)

  const year = parseIsoDate(fields.decide_date)?.year
  if (year) parts.push(String(year))

  if (fields.docket) parts.push(`Docket ${fields.docket}`)

  return parts.length > 0 ? parts.join(' • ') : null
}

function parseIsoDate(dateString: string | null): { year: number; month: number; day: number } | null {
  if (!dateString) return null

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateString)
  if (!match) return null

  const month = Number(match[2])
  if (month < 1 || month > 12) return null

  return { year: Number(match[1]), month, day: Number(match[3]) }
}
